import { useEffect, useState } from 'preact/hooks';
import './settings-panel.css';
import { useSettings } from '../../../contexts/settings-context';
import { useTheme } from '../../../hooks/use-theme';
import { SunIcon } from '../../icons/sun-icon';
import { MoonIcon } from '../../icons/moon-icon';
import { sortVoices } from '../../../utils/ttsUtils'; // Import the sorting utility

const fontOptions = [
  { value: 'default', label: 'Default' },
  { value: 'system-ui', label: 'System Default' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Merriweather, serif', label: 'Merriweather' },
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'OpenDyslexic, sans-serif', label: 'Open Dyslexic' },
];

export const SettingsPanel = () => {
  const { settings, updateSettings } = useSettings();
  const { isDarkMode, toggleTheme } = useTheme();
  const [availableVoices, setAvailableVoices] = useState<
    SpeechSynthesisVoice[]
  >([]);

  // Get available voices for text-to-speech
  useEffect(() => {
    const loadVoices = () => {
      const allVoices = window.speechSynthesis.getVoices();
      // Filter for English voices
      const englishVoices = allVoices.filter((voice) =>
        voice.lang.startsWith('en'),
      );
      console.log('Available English voices:', englishVoices);

      // Sort the voices using the utility function
      const sortedVoices = sortVoices(englishVoices);

      if (sortedVoices.length > 0) {
        setAvailableVoices(sortedVoices);
      }
    };

    // Load voices immediately (they might already be available)
    loadVoices();

    // Also listen for the voiceschanged event (voices might load asynchronously)
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);

    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
    };
  }, []);

  return (
    <div className="settings-panel">
      <h2>Settings</h2>

      <fieldset>
        <legend>Appearance</legend>
        <label className="toggle-label" onClick={toggleTheme}>
          <span>{isDarkMode ? 'Dark' : 'Light'} Mode</span>
          <span>{isDarkMode ? <MoonIcon /> : <SunIcon />}</span>
        </label>
      </fieldset>

      <fieldset>
        <legend>Text Settings</legend>
        <label>
          Font Size
          <div className="font-size-controls">
            <button
              type="button"
              className="size-button"
              onClick={() =>
                settings.textSizeIndex > 0 &&
                updateSettings({ textSizeIndex: settings.textSizeIndex - 1 })
              }
              disabled={settings.textSizeIndex <= 0}
            >
              −
            </button>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="16"
                value={settings.textSizeIndex}
                onChange={(e) =>
                  updateSettings({
                    textSizeIndex: Number(e.currentTarget.value),
                  })
                }
              />
              <span>{(settings.textSizeIndex - 8) * 10 + 100}%</span>
            </div>
            <button
              type="button"
              className="size-button"
              onClick={() =>
                settings.textSizeIndex < 16 &&
                updateSettings({ textSizeIndex: settings.textSizeIndex + 1 })
              }
              disabled={settings.textSizeIndex >= 16}
            >
              +
            </button>
          </div>
        </label>

        <label>Font Family</label>
        <select
          value={settings.fontFamily}
          onChange={(e) =>
            updateSettings({ fontFamily: e.currentTarget.value })
          }
        >
          {fontOptions.map((font) => (
            <option key={font.value} value={font.value}>
              {font.label}
            </option>
          ))}
        </select>

        <label className="toggle-label">
          <input
            type="checkbox"
            role="switch"
            aria-checked={settings.firstLineIndent}
            checked={settings.firstLineIndent}
            onChange={(e) =>
              updateSettings({ firstLineIndent: e.currentTarget.checked })
            }
          />
          <span>First Line Indent</span>
        </label>
      </fieldset>

      <fieldset>
        <legend>Text to Speech</legend>
        <label>Voice</label>
        <select
          value={settings.ttsVoice}
          onChange={(e) => updateSettings({ ttsVoice: e.currentTarget.value })}
          disabled={availableVoices.length === 0}
        >
          <option value="">System Default</option>
          {availableVoices.map((voice) => (
            <option key={voice.name} value={voice.name}>
              {voice.name} ({voice.lang})
            </option>
          ))}
        </select>

        <label>
          Reading Speed
          <div className="slider-container">
            <input
              type="range"
              min="0.2"
              max="5"
              step="0.1"
              value={settings.ttsRate}
              onChange={(e) =>
                updateSettings({
                  ttsRate: Number(e.currentTarget.value),
                })
              }
            />
            <span>{settings.ttsRate}x</span>
          </div>
        </label>
      </fieldset>
    </div>
  );
};
